import { TSlugStatus } from 'app/types/TSlugStatus';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useEffect, useRef, useState } from 'react';

const SLUG_PATTERN = /^[a-z0-9-]+$/;
const DEFAULT_DELAY_MS = 400;

type TCheckedStatus = 'idle' | 'unchanged' | 'available' | 'taken' | 'invalid' | 'error';

type TOptions = {
  slug: string;
  checkAvailability: (slug: string) => Promise<boolean>;
  currentSlug?: string;
  enabled?: boolean;
  delayMs?: number;
};

export const useSlugAvailability = ({
  slug,
  checkAvailability,
  currentSlug,
  enabled = true,
  delayMs = DEFAULT_DELAY_MS,
}: TOptions) => {
  const [checkedStatus, setCheckedStatus] = useState<TCheckedStatus>('idle');
  const [isChecking, setIsChecking] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const checkAvailabilityRef = useRef(checkAvailability);
  checkAvailabilityRef.current = checkAvailability;

  const debouncedSlug = useDebouncedValue(slug, delayMs);
  const isUnverified = isChecking || debouncedSlug !== slug;

  useEffect(() => {
    if (!enabled || !debouncedSlug) {
      setCheckedStatus('idle');
      setIsChecking(false);
      return;
    }

    if (debouncedSlug === currentSlug) {
      setCheckedStatus('unchanged');
      setIsChecking(false);
      return;
    }

    if (!SLUG_PATTERN.test(debouncedSlug)) {
      setCheckedStatus('invalid');
      setIsChecking(false);
      return;
    }

    let isCurrentSlug = true;
    setIsChecking(true);

    const check = async () => {
      let nextStatus: TCheckedStatus;
      try {
        nextStatus = (await checkAvailabilityRef.current(debouncedSlug)) ? 'available' : 'taken';
      } catch {
        nextStatus = 'error';
      }
      if (!isCurrentSlug) {
        return;
      }
      setCheckedStatus(nextStatus);
      setIsChecking(false);
    };

    check();

    return () => {
      isCurrentSlug = false;
    };
  }, [debouncedSlug, currentSlug, enabled, attempt]);

  const resolveStatus = (): TSlugStatus => {
    if (!enabled || !slug || slug === currentSlug) {
      return 'idle';
    }
    if (isUnverified) {
      return 'checking';
    }
    if (checkedStatus === 'idle' || checkedStatus === 'unchanged') {
      return 'idle';
    }
    return checkedStatus;
  };

  const retry = () => setAttempt((value) => value + 1);

  return {
    status: resolveStatus(),
    isAvailable: checkedStatus === 'available' || checkedStatus === 'unchanged',
    isUnverified,
    retry,
  };
};
